/**
 * Domain-scoped routing-topic enum for the **storage** vertical.
 *
 * `RoutingTopic` is the operational dimension the routing system keys
 * off of — "which team should this escalation go to?" It's deliberately
 * separate from the Cortex catalog (which organizes operator-facing
 * policy knowledge): Cortex evolves with UI/knowledge work; routing
 * topics evolve with how the business is staffed.
 *
 * The bot classifies every escalation into one of these via the
 * `escalate_to_team` tool. Operators map each value to a routing group
 * on the Settings → Routing Groups page; per-scope overrides live on
 * the PM Escalation & Ticketing page.
 *
 * Future verticals (lawn-care, hospitality, etc.) will export their
 * own `*RoutingTopic` enum from this folder; the routing engine reads
 * a generic string column so the same infra serves all of them.
 */
export type StorageRoutingTopic =
  | "sales" // pre-customer: reservations, pricing, referrals, commercial accounts
  | "retention" // existing-customer save / cancellation prevention
  | "service" // existing-tenant general support, billing, account changes (default fallback)
  | "maintenance" // facility issues: locks, gates, unit problems, broken equipment
  | "claims" // damage / theft / break-in / water damage / insurance claims
  | "emergency"; // life-safety: trapped at gate, fire, medical, break-in-in-progress

export const STORAGE_ROUTING_TOPICS: ReadonlyArray<{ id: StorageRoutingTopic; label: string; description: string }> = [
  { id: "sales", label: "Sales", description: "Pre-customer: reservations, pricing, referrals, commercial accounts." },
  { id: "retention", label: "Retention", description: "Existing-customer save / cancellation prevention. Tenants asking about leaving." },
  { id: "service", label: "Service", description: "Existing-tenant general support, billing, account changes. Default fallback." },
  { id: "maintenance", label: "Maintenance", description: "Facility issues: locks, gates, unit problems, broken equipment." },
  { id: "claims", label: "Claims", description: "Damage / theft / break-in / water damage / insurance claims." },
  { id: "emergency", label: "Emergency", description: "Life-safety: trapped at gate, fire, medical, break-in-in-progress." }
];

export function isStorageRoutingTopic(value: unknown): value is StorageRoutingTopic {
  return typeof value === "string" && STORAGE_ROUTING_TOPICS.some((t) => t.id === value);
}
